import { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { MapPin, ArrowRight } from "lucide-react";
import { getPoojas, createRequest, apiErr } from "../lib/api";
import { useCustomer } from "../lib/auth";
import LocationPicker from "../components/LocationPicker";

const TIMES = ["06:00", "09:00", "16:00", "19:00"];

function nextDays(n) {
  const out = [];
  const now = new Date();
  for (let i = 0; i < n; i++) {
    const d = new Date(now); d.setDate(now.getDate() + i);
    out.push({
      iso: d.toISOString().slice(0, 10),
      dow: d.toLocaleDateString("en-US", { weekday: "short" }),
      day: d.getDate(),
    });
  }
  return out;
}

export default function RequestPooja() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { user, setAuthOpen } = useCustomer();
  const [poojas, setPoojas] = useState([]);
  const [slug, setSlug] = useState(params.get("poojaSlug") || "");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [address, setAddress] = useState("");
  const [landmark, setLandmark] = useState("");
  const [coords, setCoords] = useState({ lat: null, lng: null });
  const [bMin, setBMin] = useState("");
  const [bMax, setBMax] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const days = useMemo(() => nextDays(8), []);

  useEffect(() => { getPoojas().then((d) => setPoojas(d.poojas)); }, []);

  const submit = async () => {
    if (!user) { setAuthOpen(true); return; }
    const pooja = poojas.find((p) => p.slug === slug);
    if (!slug || !date || !time || !address) { toast.error("Please fill ceremony, date, time and address"); return; }
    setBusy(true);
    try {
      await createRequest({
        pooja_slug: slug, pooja_name: pooja?.name || slug, date, time,
        address, landmark: landmark || null,
        lat: coords.lat, lng: coords.lng,
        budget_min: bMin ? Number(bMin) : null, budget_max: bMax ? Number(bMax) : null,
        note: note || null,
      });
      toast.success("Request sent — purohits will send proposals");
      nav("/app/bookings");
    } catch (e) { toast.error(apiErr(e, "Could not create request")); }
    finally { setBusy(false); }
  };

  const field = "w-full rounded-xl border border-[#E4D8C7] bg-white px-4 py-3 text-sm outline-none focus:border-[#8B1A2B]";

  return (
    <div className="mx-auto max-w-3xl px-4 pt-5 pc-fade-in" data-testid="cust-request">
      <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#8B1A2B]">Request proposals</p>
      <h1 className="mt-1 font-heading text-2xl text-[#2A211C]">Let the right purohits come to you.</h1>
      <p className="mt-1 text-sm text-[#7A6E64]">Verified priests respond with their price, availability, and a personal note.</p>

      {/* ceremony */}
      <label className="mt-5 block text-sm font-semibold text-[#2A211C]">Ceremony *</label>
      <div className="mt-2 flex flex-wrap gap-2">
        {poojas.map((p) => (
          <button key={p.slug} onClick={() => setSlug(p.slug)} data-testid={`req-pooja-${p.slug}`}
            className={`rounded-full px-3 py-1.5 text-sm font-medium ${slug === p.slug ? "bg-[#8B1A2B] text-white" : "border border-[#E7DCCB] bg-white text-[#5C5149]"}`}>
            {p.name}
          </button>
        ))}
      </div>

      {/* when */}
      <label className="mt-5 block text-sm font-semibold text-[#2A211C]">When *</label>
      <div className="mt-2 grid grid-cols-8 gap-1.5">
        {days.map((d) => (
          <button key={d.iso} onClick={() => setDate(d.iso)} data-testid={`req-date-${d.iso}`}
            className={`flex flex-col items-center rounded-xl py-2 text-xs ${date === d.iso ? "bg-[#8B1A2B] text-white" : "border border-[#E7DCCB] bg-white text-[#5C5149]"}`}>
            <span className="opacity-70">{d.dow}</span>
            <span className="text-sm font-semibold">{d.day}</span>
          </button>
        ))}
      </div>
      <div className="mt-2 flex flex-wrap gap-2">
        {TIMES.map((t) => (
          <button key={t} onClick={() => setTime(t)} data-testid={`req-time-${t}`}
            className={`rounded-full px-4 py-1.5 text-sm font-medium ${time === t ? "bg-[#8B1A2B] text-white" : "border border-[#E7DCCB] bg-white text-[#5C5149]"}`}>{t}</button>
        ))}
      </div>

      {/* where */}
      <label className="mt-5 block text-sm font-semibold text-[#2A211C]">Where *</label>
      <div className="mt-2 flex items-center gap-2 rounded-xl border border-[#E4D8C7] bg-[#F5EFE4] px-3 py-2 text-xs text-[#7A6E64]">
        <MapPin size={14} className="text-[#8B1A2B]" /> Pin the exact spot so your purohith arrives right on time.
      </div>
      <LocationPicker onChange={({ lat, lng, address: addr }) => {
        setCoords({ lat, lng });
        if (addr) setAddress(addr);
      }} />
      <input value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Flat, street, area, Bengaluru" data-testid="req-address" className={`${field} mt-2`} />
      <input value={landmark} onChange={(e) => setLandmark(e.target.value)} placeholder="Landmark, apartment, gate number" data-testid="req-landmark" className={`${field} mt-2`} />

      {/* budget */}
      <label className="mt-5 block text-sm font-semibold text-[#2A211C]">Budget range <span className="font-normal text-[#A0968A]">optional</span></label>
      <div className="mt-2 flex gap-2">
        <input value={bMin} onChange={(e) => setBMin(e.target.value)} inputMode="numeric" placeholder="Min ₹" data-testid="req-budget-min" className={field} />
        <input value={bMax} onChange={(e) => setBMax(e.target.value)} inputMode="numeric" placeholder="Max ₹" data-testid="req-budget-max" className={field} />
      </div>
      <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="A note for purohits (optional)" data-testid="req-note" className={`${field} mt-3`} />

      <div className="mt-5 rounded-2xl border border-[#EBE0D0] bg-white p-4">
        <p className="font-semibold text-[#2A211C]">No payment before bids</p>
        <ol className="mt-2 space-y-1 text-sm text-[#7A6E64]">
          <li>1. Share ceremony, timing, address, and budget</li>
          <li>2. Eligible purohits submit their prices</li>
          <li>3. Choose one proposal, then pay securely with Cashfree</li>
        </ol>
      </div>

      <button onClick={submit} disabled={busy} data-testid="req-submit"
        className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-[#8B1A2B] py-3.5 font-semibold text-white hover:bg-[#6F1420] disabled:opacity-60">
        {busy ? "Sending…" : "Request proposals"} <ArrowRight size={16} />
      </button>
    </div>
  );
}
